<template>
    <vx-expandable-card>
        <template v-slot:header> Message Variable Fallbacks </template>
        <template v-slot:headerSubtitle>
            The Message Builder embeds a fallback into each inserted variable, so a blank value renders the fallback
            instead of nothing. Changing one later only affects newly inserted variables.
        </template>

        <template v-slot:content>
            <vx-icon-loading v-if="loading" spin></vx-icon-loading>
            <vx-alert v-else-if="loadFailed" color="danger" :closeable="false" data-test="fallbacks-load-failed">
                The fallbacks could not be loaded. Reload the page to edit them.
            </vx-alert>
            <form v-else class="flex flex-col gap-6 mt-3" @submit.prevent="submit">
                <div class="flex flex-col gap-4">
                    <vx-label size="md">Contact fields</vx-label>

                    <div v-for="field in CONTACT_FIELDS" :key="field.key">
                        <vx-label>{{ field.label }}</vx-label>
                        <vx-input
                            size="lg"
                            v-model="data.fixed[field.key]"
                            :disabled="props.disabled"
                            :data-test="fieldTestId(field.key)"
                            :error="displayFirstError(validation.fixed[field.key])"
                        ></vx-input>
                    </div>

                    <message-variable-fallback-rows
                        v-model="data.contactCustomAttributes"
                        label="Contact custom attributes"
                        :keyRule="customAttributeKey"
                        :keyMaxLength="80"
                        emptyMessage="No contact custom attribute fallbacks were added to this team."
                        :disabled="props.disabled"
                        :limitReached="limitReached"
                        data-test="contact-custom-attribute-rows"
                    ></message-variable-fallback-rows>
                </div>

                <div v-if="FEATURES.contact_groups" class="flex flex-col gap-4">
                    <vx-label size="md">Group fields</vx-label>

                    <div>
                        <vx-label>Group name</vx-label>
                        <vx-input
                            size="lg"
                            v-model="data.fixed[FALLBACK_KEY_GROUP_NAME]"
                            :disabled="props.disabled"
                            :data-test="fieldTestId(FALLBACK_KEY_GROUP_NAME)"
                            :error="displayFirstError(validation.fixed[FALLBACK_KEY_GROUP_NAME])"
                        ></vx-input>
                    </div>

                    <message-variable-fallback-rows
                        v-model="data.groupCustomAttributes"
                        label="Group custom attributes"
                        :keyRule="groupCustomAttributeKey"
                        :keyMaxLength="40"
                        emptyMessage="No group custom attribute fallbacks were added to this team."
                        :disabled="props.disabled"
                        :limitReached="limitReached"
                        data-test="group-custom-attribute-rows"
                    ></message-variable-fallback-rows>
                </div>

                <vx-button
                    block
                    size="lg"
                    type="submit"
                    :loading="processing"
                    :disabled="props.disabled"
                    data-test="fallbacks-save"
                >
                    Save
                </vx-button>
            </form>
        </template>
    </vx-expandable-card>
</template>

<script setup lang="ts">
import { VxAlert, VxButton, VxExpandableCard, VxIconLoading, VxInput, VxLabel } from '@voxie/frontend-components';
import { computed, inject, onMounted, ref } from 'vue';
import { maxLength } from '@vuelidate/validators';
import MessageVariableFallbackRows from './MessageVariableFallbackRows.vue';
import { useFormSubmission } from '~/composables/useFormSubmission';
import { useToasted } from '~/composables/useToasted';
import { messageVariableFallbacksClient } from '~/services';
import type { MessageVariableFallback, MessageVariableFallbackRow } from '~/services/messageVariableFallbacksClient.types';
import type { Features } from '~/provides/features.types';
import {
    CONTACT_CUSTOM_ATTRIBUTE_FALLBACK_PREFIX,
    FALLBACK_KEY_CONTACT_EMAIL,
    FALLBACK_KEY_CONTACT_ENTRY_CHANNEL,
    FALLBACK_KEY_CONTACT_FIRST_NAME,
    FALLBACK_KEY_CONTACT_LAST_NAME,
    FALLBACK_KEY_CONTACT_LIFECYCLE_STATUS,
    FALLBACK_KEY_CONTACT_MEMBERSHIP_STATUS,
    FALLBACK_KEY_GROUP_NAME,
    GROUP_CUSTOM_ATTRIBUTE_FALLBACK_PREFIX,
    MAX_FALLBACKS_PER_TEAM,
    MAX_FALLBACK_VALUE_LENGTH,
    contactCustomAttributeFallbackKey,
    groupCustomAttributeFallbackKey,
} from '~/utils/messageVariableFallbacks';
import { catchRequestError } from '~/utils/request';
import {
    csvInjection,
    customAttributeKey,
    customAttributeValue,
    displayFirstError,
    groupCustomAttributeKey,
    liquidStringLiteral,
} from '~/utils/validation';

interface FallbacksForm extends Record<string, unknown> {
    fixed: Record<string, string>;
    contactCustomAttributes: MessageVariableFallbackRow[];
    groupCustomAttributes: MessageVariableFallbackRow[];
}

const props = defineProps<{
    teamId: number;
    disabled?: boolean;
}>();

const FEATURES = inject<Features>('FEATURES')!;
const toasted = useToasted();

const CONTACT_FIELDS = [
    { key: FALLBACK_KEY_CONTACT_FIRST_NAME, label: 'First name' },
    { key: FALLBACK_KEY_CONTACT_LAST_NAME, label: 'Last name' },
    { key: FALLBACK_KEY_CONTACT_EMAIL, label: 'Email' },
    { key: FALLBACK_KEY_CONTACT_ENTRY_CHANNEL, label: 'Entry channel' },
    { key: FALLBACK_KEY_CONTACT_MEMBERSHIP_STATUS, label: 'Membership status' },
    { key: FALLBACK_KEY_CONTACT_LIFECYCLE_STATUS, label: 'Lifecycle status' },
];
const FIXED_KEYS = [...CONTACT_FIELDS.map((field) => field.key), FALLBACK_KEY_GROUP_NAME];

const emptyForm = (): FallbacksForm => ({
    fixed: Object.fromEntries(FIXED_KEYS.map((key) => [key, ''])),
    contactCustomAttributes: [],
    groupCustomAttributes: [],
});

const data = ref<FallbacksForm>(emptyForm());
const loading = ref(true);
const loadFailed = ref(false);

const fieldTestId = (key: string): string => `fallback-${key.replace(/[._]/g, '-')}`;

const valueRules = {
    maxLength: maxLength(MAX_FALLBACK_VALUE_LENGTH),
    customAttributeValue,
    csvInjection,
    liquidStringLiteral,
};

const toForm = (fallbacks: MessageVariableFallback[]): FallbacksForm => {
    const form = emptyForm();

    fallbacks.forEach(({ variable_key: variableKey, fallback_value: value }) => {
        if (Object.hasOwn(form.fixed, variableKey)) {
            form.fixed[variableKey] = value;
        } else if (variableKey.startsWith(CONTACT_CUSTOM_ATTRIBUTE_FALLBACK_PREFIX)) {
            form.contactCustomAttributes.push({ key: variableKey.slice(CONTACT_CUSTOM_ATTRIBUTE_FALLBACK_PREFIX.length), value });
        } else if (variableKey.startsWith(GROUP_CUSTOM_ATTRIBUTE_FALLBACK_PREFIX)) {
            form.groupCustomAttributes.push({ key: variableKey.slice(GROUP_CUSTOM_ATTRIBUTE_FALLBACK_PREFIX.length), value });
        }
    });

    return form;
};

// Group rows left from when the team had the feature are hidden and the server would refuse them, so they
// are dropped from the save rather than blocking it.
const toFallbacks = (form: FallbacksForm): MessageVariableFallback[] => [
    ...Object.entries(form.fixed)
        .filter(([, value]) => value.trim() !== '')
        .filter(([variableKey]) => FEATURES.contact_groups || !variableKey.startsWith('group.'))
        .map(([variableKey, value]) => ({ variable_key: variableKey, fallback_value: value })),
    ...form.contactCustomAttributes.map((row) => ({
        variable_key: contactCustomAttributeFallbackKey(row.key),
        fallback_value: row.value,
    })),
    ...(FEATURES.contact_groups ? form.groupCustomAttributes : []).map((row) => ({
        variable_key: groupCustomAttributeFallbackKey(row.key),
        fallback_value: row.value,
    })),
];

// The server caps the whole set, fixed fields included, so both row blocks share one count.
const limitReached = computed(() => toFallbacks(data.value).length >= MAX_FALLBACKS_PER_TEAM);

const { validation, submit, processing } = useFormSubmission({
    data,
    validation: {
        fixed: Object.fromEntries(FIXED_KEYS.map((key) => [key, valueRules])),
    },
    submit: () =>
        messageVariableFallbacksClient.updateMessageVariableFallbacks(props.teamId, {
            fallbacks: toFallbacks(data.value),
        }),
    notification: () => ({ message: 'Message variable fallbacks saved' }),
    preserveScroll: true,
});

onMounted(() => {
    messageVariableFallbacksClient
        .getMessageVariableFallbacks(props.teamId)
        .then((response) => {
            data.value = toForm(response.data.data);
            loading.value = false;
        })
        .catch((error) => {
            // saving the empty form a failed load leaves behind would delete every fallback
            loadFailed.value = true;
            loading.value = false;
            toasted.error(catchRequestError(error));
        });
});
</script>
